import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { BsBell, BsBellFill } from "react-icons/bs";
import { HiX } from "react-icons/hi";
import { formatNumber } from "../utils/numbers";

const BASE = "https://eps.elkomprintama.com/";

function NotificationBell() {
  const { user, permissions } = useSelector((state) => state.auth);
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const canSee =
    user?.user_lvl === 1 ||
    permissions === "admin" ||
    (Array.isArray(permissions) &&
      permissions.includes("notifications.stock_alerts"));

  const fetchAlerts = () => {
    if (!user?.username) return;
    axios
      .get(
        `${BASE}get/api/store/alerts?username=${encodeURIComponent(user.username)}`,
      )
      .then((res) => setAlerts(res.data.alerts || []))
      .catch(() => {});
  };

  useEffect(() => {
    if (!canSee) return;
    fetchAlerts();
    const iv = setInterval(fetchAlerts, 60_000);
    return () => clearInterval(iv);
  }, [user?.username, canSee]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const dismiss = async (ids) => {
    try {
      await axios.patch(`${BASE}patch/api/store/alerts/read`, {
        alert_ids: ids,
        username: user?.username,
      });
      if (ids === "all") {
        setAlerts([]);
      } else {
        setAlerts((prev) => prev.filter((a) => !ids.includes(a.alert_id)));
      }
    } catch {}
  };

  const count = alerts.length;

  if (!canSee) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        title="Stock Alerts"
      >
        {count > 0 ? (
          <BsBellFill className="text-amber-400" size={18} />
        ) : (
          <BsBell className="text-gray-400" size={18} />
        )}
        {count > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[16px] h-4 rounded-full bg-red-500 border-2 border-gray-900 flex items-center justify-center text-white font-bold px-0.5"
            style={{ fontSize: "9px" }}
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 w-96 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-amber-100 bg-amber-50">
            <p className="text-amber-800 text-sm font-bold uppercase tracking-wide">
              Stock Alerts{count > 0 ? ` (${count})` : ""}
            </p>
            {count > 0 && (
              <button
                onClick={() => dismiss("all")}
                className="text-sm text-amber-600 hover:text-amber-800 font-semibold underline"
              >
                Dismiss all
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto">
            {count === 0 ? (
              <div className="px-4 py-10 flex flex-col items-center gap-2">
                <BsBell className="text-gray-300" size={28} />
                <p className="text-gray-400 text-sm italic">No alerts</p>
              </div>
            ) : (
              alerts.map((a) => (
                <div
                  key={a.alert_id}
                  className="flex items-start gap-3 px-4 py-3.5 border-b border-gray-100 hover:bg-red-50 transition-colors"
                >
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0 mt-1.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 text-sm font-semibold leading-snug">
                      {a.product_name}
                    </p>
                    <p className="text-gray-500 text-xs mt-0.5">
                      {a.product_code}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">
                        Stock: {formatNumber(a.current_stock)} {a.unit}
                      </span>
                      <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                        Min: {formatNumber(a.min_stock)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => dismiss([a.alert_id])}
                    className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors mt-0.5"
                    title="Dismiss"
                  >
                    <HiX size={17} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
